import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AUTH_CHANNEL,
  deleteAccount as deleteAccountRequest,
  fetchAccount,
  openSignIn,
  pushName,
  signInUrl,
  signOut as signOutRequest,
  type AccountUser,
  type AuthMessage,
  type DeleteOutcome,
  type SignInOptions,
} from '../lib/account';
import { syncOperator } from '../lib/operator';
import { getStrings } from '../lib/strings';
import type { ToastFn } from '../App';

/**
 * Who is signed in, for the whole tab.
 *
 * Asked once on load, then again whenever the sign-in window says it is
 * done, and once more when this window gets focus back after a sign-in was
 * started — the belt to the channel's braces, for a browser where the
 * channel does not reach. `enabled` is false until the Worker says Google
 * is configured, and the whole feature is invisible while it is.
 *
 * The three sentences this file says are read with `getStrings()` and not
 * with `useStrings()`, even though this is a hook. Every one of them is
 * raised from a callback that fires long after the render that installed it
 * — a message on the broadcast channel, a window regaining focus, a click on
 * Sign out — and the effect that arms the first two runs once. A catalogue
 * captured at render would be the language the tab was in at mount, and
 * would go on being that language after the switch was used, silently. This
 * is the case the comment on `getStrings` is about.
 */
export interface AccountApi {
  enabled: boolean;
  /** Whether this deployment takes pictures in the chat. Not an account fact;
   *  it rides on the same call because that call is already the tab's one
   *  question about how this deployment is configured. */
  pictures: boolean;
  user: AccountUser | null;
  /** True while a sign-in window is open or a request is in flight. */
  busy: boolean;
  /**
   * True once the Worker has answered for the first time, whatever it said.
   *
   * `enabled` and `user` both start at the signed-out answer, which is the
   * right default for anything that only hides itself — the sign-in button on
   * the level list simply appears. It is the wrong default for anything that
   * has to choose between two doors: the chat panel would offer a listener the
   * name prompt, and replace it a moment later with the sign-in one, which is
   * long enough to be read and even typed into. Whoever must not guess waits
   * on this instead.
   */
  loaded: boolean;
  signIn(options?: SignInOptions): void;
  signOut(): Promise<void>;
  /**
   * Be called something else, on the account and so in every room.
   *
   * Null puts the Google name back, which is a real answer rather than an
   * absence (worker/migrations/0017_chosen_name.sql). What comes back is the
   * name the account holds afterwards, or null when the server refused it or
   * could not be reached -- and nothing on screen has changed in that case,
   * because there is nothing here to change it optimistically with: a name is
   * what other people call you, and the copy that decides is the one the rooms
   * read on the upgrade.
   */
  rename(name: string | null): Promise<string | null>;
  /**
   * Close the account, on the server and in this tab.
   *
   * The only irreversible thing in this interface, and it is a plain method
   * beside the others because the safeguards are not here: the screen that
   * calls it is a confirmation the learner had to read and type into, and the
   * Worker refuses a request that does not name the account it is deleting.
   * A guard in this hook as well would be a third opinion on a decision two
   * places have already made properly.
   *
   * `forfeit` is the learner having been told a purchase goes with it. False
   * on the first press, and the answer `'entitled'` is the server asking for a
   * second one — see lib/account.ts on why that is not a failure.
   *
   * On `'gone'` the signed-out state is set here rather than by a refresh,
   * for the reason `rename` sets a name here: `/api/auth/me` would say the
   * same thing one round trip later, and a screen that went on showing an
   * account for that round trip would be showing one that no longer exists.
   */
  deleteAccount(forfeit: boolean): Promise<DeleteOutcome>;
}

export function useAccount(notify: ToastFn): AccountApi {
  const [enabled, setEnabled] = useState(false);
  const [pictures, setPictures] = useState(false);
  const [user, setUser] = useState<AccountUser | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const userRef = useRef<AccountUser | null>(null);
  userRef.current = user;
  const awaiting = useRef(false);

  const refresh = useCallback(async () => {
    const state = await fetchAccount();
    setEnabled(state.enabled);
    setPictures(state.pictures);
    setUser(state.user);
    // The figure and the account, reconciled: the account's wins where it has
    // one, and this browser's is pushed up where it does not (lib/operator.ts).
    // Started and not awaited — nothing on this path is waiting to draw an
    // avatar, and a learner whose network is down still has theirs.
    void syncOperator(state.user?.avatarSeed ?? null, state.user !== null);
    // Set after the answer rather than in a `finally`: `fetchAccount` already
    // turns a failed request into the signed-out answer, so there is no case
    // where this is reached without one.
    setLoaded(true);
    return state;
  }, []);

  useEffect(() => {
    let live = true;
    void refresh();

    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(AUTH_CHANNEL);
      channel.onmessage = (ev: MessageEvent<AuthMessage>) => {
        if (!live || !ev.data || typeof ev.data !== 'object') return;
        if (ev.data.t === 'signed-in') {
          awaiting.current = false;
          setBusy(true);
          void refresh().then((s) => {
            if (!live) return;
            setBusy(false);
            if (s.user) notifyRef.current(getStrings().standing.account.toastSignedIn(s.user.name));
          });
        } else if (ev.data.t === 'failed') {
          awaiting.current = false;
          setBusy(false);
          notifyRef.current(ev.data.reason, 'error');
        }
      };
    } catch {
      channel = null;
    }

    const onFocus = () => {
      if (!awaiting.current) return;
      // Back from the sign-in window, one way or another. Ask, and if there
      // is still no user, keep waiting: a person may only have switched tabs.
      void refresh().then((s) => {
        if (!live) return;
        if (s.user && !userRef.current) notifyRef.current(getStrings().standing.account.toastSignedIn(s.user.name));
        if (s.user) {
          awaiting.current = false;
          setBusy(false);
        }
      });
    };
    window.addEventListener('focus', onFocus);

    return () => {
      live = false;
      channel?.close();
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const signIn = useCallback((options?: SignInOptions) => {
    // Read rather than passed through: some buttons hand this straight to
    // `onClick`, and a click event is not a set of options.
    const asked: SignInOptions = options && typeof options === 'object' && 'guest' in options ? { guest: options.guest === true } : {};
    if (!openSignIn(asked)) {
      // No window allowed: the redirect it is. The running VM is lost, which
      // is why this is the fallback and not the rule.
      window.location.assign(signInUrl(false, asked));
      return;
    }
    awaiting.current = true;
    setBusy(true);
    // A window closed without finishing never reports. Stop looking busy
    // after a while; the focus handler is still armed if it finishes late.
    window.setTimeout(() => setBusy(false), 60_000);
  }, []);

  const signOut = useCallback(async () => {
    setBusy(true);
    await signOutRequest();
    setUser(null);
    setBusy(false);
    notifyRef.current(getStrings().standing.account.toastSignedOut);
  }, []);

  const rename = useCallback(async (next: string | null) => {
    // Nobody to rename. The screen offering this hides it for a signed-out
    // learner, whose name is this browser's and is renamed through the store
    // that owns it (lib/naming.ts), so reaching here is a bug rather than a
    // state -- and a PUT would be answered 401 anyway.
    if (!userRef.current) return null;
    const name = await pushName(next);
    if (name === null) return null;
    // The account, not a refresh: `/api/auth/me` would say the same thing one
    // round trip later, and this value came out of the write itself. Every
    // screen holding the account re-renders with it, which is what carries a
    // rename into the chat sockets (state/useChat.ts, `generation`).
    setUser((was) => (was ? { ...was, name } : was));
    return name;
  }, []);

  const deleteAccount = useCallback(async (forfeit: boolean) => {
    // Nobody to delete. The screen offering this is only drawn for a signed-in
    // learner, so reaching here is a bug rather than a state -- and the route
    // would answer 401 anyway.
    if (!userRef.current) return 'failed' as const;
    setBusy(true);
    const outcome = await deleteAccountRequest(userRef.current.id, forfeit);
    setBusy(false);
    if (outcome !== 'gone') return outcome;
    setUser(null);
    // The toast is the profile catalogue's, not the account menu's, because
    // this is the only screen that can raise it and the sentence is about the
    // record rather than about the corner of the level list. Read with
    // `getStrings`, like the other three here, for the reason at the top of
    // this file: it is said from a callback long after the render that armed
    // it, and a catalogue captured at render would be the language the tab
    // was in at mount.
    notifyRef.current(getStrings().profile.deletion.toastGone);
    return outcome;
  }, []);

  return { enabled, pictures, user, busy, loaded, signIn, signOut, rename, deleteAccount };
}
